import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Linkedin, MapPin, Send, CalendarCheck, MonitorPlay, MessageSquare } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { COMPANY } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "consultation", label: "Book Consultation", icon: CalendarCheck },
  { id: "demo", label: "Request Demo", icon: MonitorPlay },
  { id: "general", label: "General Enquiry", icon: MessageSquare },
];

const initialForm = { name: "", email: "", company: "", phone: "", message: "" };

const Contact = () => {
  const [tab, setTab] = useState("consultation");
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, type: tab });
      toast.success("Thank you — your enquiry has been received. We'll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s/g, "")}` },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: Linkedin, label: "LinkedIn", value: "TechSpace Solutions", href: COMPANY.linkedin },
    { icon: MapPin, label: "Office", value: COMPANY.address, href: "#map" },
  ];

  return (
    <section id="contact" className="relative py-24 sm:py-32 lg:py-40" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-8 lg:px-12">
        <Reveal className="mb-16 max-w-3xl">
          <Eyebrow>08 — Contact</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Let's build something intelligent together.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12">
          {/* Form */}
          <Reveal className="lg:col-span-3">
            <div className="rounded-3xl border border-border bg-card p-6 shadow-[0_8px_40px_rgb(0,0,0,0.05)] sm:p-10">
              <div className="mb-8 flex flex-wrap gap-2">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      data-testid={`contact-tab-${t.id}`}
                      className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${
                        tab === t.id
                          ? "bg-navy text-white"
                          : "border border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full name *</Label>
                    <Input id="name" data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Jane Doe" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={update("email")} placeholder="jane@company.com" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company</Label>
                    <Input id="company" data-testid="input-company" value={form.company} onChange={update("company")} placeholder="Company Ltd" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="+44 ..." className="rounded-xl" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">How can we help? *</Label>
                  <Textarea id="message" data-testid="input-message" value={form.message} onChange={update("message")} placeholder="Tell us about your project or the product you're interested in..." rows={5} className="rounded-xl" />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group h-12 w-full rounded-full bg-teal text-base font-semibold text-teal-foreground hover:bg-teal/90 sm:w-auto sm:px-8"
                >
                  {loading ? "Sending..." : "Send Enquiry"}
                  <Send className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </form>
            </div>
          </Reveal>

          {/* Contact info + map */}
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="flex h-full flex-col gap-4">
              {contactItems.map((c) => {
                const Icon = c.icon;
                return (
                  <a
                    key={c.label}
                    href={c.href}
                    target={c.label === "LinkedIn" ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={`contact-${c.label.toLowerCase()}`}
                    className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-teal"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal/10 text-teal">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</div>
                      <div className="text-sm font-medium text-foreground">{c.value}</div>
                    </div>
                  </a>
                );
              })}
              <div id="map" className="mt-auto overflow-hidden rounded-2xl border border-border">
                <iframe
                  title="TechSpace Solutions location"
                  src="https://www.google.com/maps?q=Canary+Wharf+London&output=embed"
                  className="h-56 w-full grayscale-[0.3]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
